import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X, Layers } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

/**
 * MultiPlatformPicker
 *
 * Multi-select combobox for a product's `platform_ids` field, grouped by
 * category (like the existing single-select but with checkboxes and
 * order-preserved chip stack below).
 *
 * Props:
 *  - value: string[]                       currently selected platform ids
 *  - platformsByCategory: [catName, {color, items:[{id, name, short}]}]
 *  - onChange: (nextIds: string[]) => void
 *  - disabled?: boolean
 *  - testId?: string                       root data-testid
 */
export default function MultiPlatformPicker({
  value = [],
  platformsByCategory = [],
  onChange,
  disabled = false,
  testId = 'multi-platform-picker',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const allPlatforms = useMemo(() => {
    const out = [];
    platformsByCategory.forEach(([catName, cat]) => {
      (cat.items || []).forEach(pl => out.push({ ...pl, catName, catColor: cat.color }));
    });
    return out;
  }, [platformsByCategory]);

  const idToPlatform = useMemo(() => {
    const m = {};
    allPlatforms.forEach(p => { m[p.id] = p; });
    return m;
  }, [allPlatforms]);

  const toggle = (id) => {
    if (!onChange) return;
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  const remove = (id) => onChange && onChange(value.filter(v => v !== id));

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return platformsByCategory;
    return platformsByCategory
      .map(([catName, cat]) => [catName, {
        ...cat,
        items: (cat.items || []).filter(p =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.short || '').toLowerCase().includes(q) ||
          catName.toLowerCase().includes(q)
        ),
      }])
      .filter(([, cat]) => cat.items.length > 0);
  }, [platformsByCategory, query]);

  return (
    <div className="space-y-2" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between h-9 font-normal"
            data-testid={`${testId}-trigger`}
          >
            <span className="text-muted-foreground text-sm truncate">
              {value.length === 0
                ? 'Select platform(s)…'
                : `${value.length} platform${value.length === 1 ? '' : 's'} selected`}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[--radix-popover-trigger-width] p-0 max-h-[380px] overflow-hidden"
          data-testid={`${testId}-popover`}
        >
          <div className="p-2 border-b sticky top-0 bg-card z-10">
            <Input
              placeholder="Search platforms…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-xs"
              autoFocus
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto max-h-[300px]">
            {filteredGroups.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No platforms match “{query}”
              </div>
            ) : (
              filteredGroups.map(([catName, cat]) => (
                <div key={catName} className="py-1">
                  <div className="px-3 py-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-muted/40">
                    <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                    {catName}
                  </div>
                  {(cat.items || []).map(pl => {
                    const on = value.includes(pl.id);
                    return (
                      <button
                        key={pl.id}
                        type="button"
                        onClick={() => toggle(pl.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${on ? 'bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100' : 'hover:bg-muted'}`}
                        data-testid={`${testId}-option-${pl.id}`}
                      >
                        <span className={`inline-flex items-center justify-center h-4 w-4 rounded-sm border shrink-0 ${on ? 'border-violet-500 bg-violet-500 text-white' : 'border-muted-foreground/40'}`}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="flex-1 truncate">{pl.name || pl.short}</span>
                        {pl.short && pl.name !== pl.short && (
                          <span className="text-[10px] text-muted-foreground shrink-0">{pl.short}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          {value.length > 0 && (
            <div className="border-t p-2 flex items-center justify-between text-[11px] bg-muted/30">
              <span className="text-muted-foreground">{value.length} selected</span>
              <button
                onClick={() => onChange && onChange([])}
                className="text-violet-600 hover:underline"
                data-testid={`${testId}-clear`}
              >
                Clear all
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Selected chip stack — order preserved */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {value.map(id => {
            const p = idToPlatform[id];
            if (!p) return null;
            return (
              <Badge
                key={id}
                variant="outline"
                className="gap-1 pl-2 pr-1 py-0.5 text-[11px] border-slate-300"
                data-testid={`${testId}-chip-${id}`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: p.catColor }} />
                <span>{p.name || p.short}</span>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="ml-0.5 h-3.5 w-3.5 inline-flex items-center justify-center rounded-sm hover:bg-rose-100 hover:text-rose-600"
                  data-testid={`${testId}-chip-remove-${id}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Static badge cluster used to *display* a product's platform list on
 * the detail pane or a list row. Use `size="sm"` for list rows.
 */
export function PlatformBadgeStack({ platforms = [], size = 'md', maxInline = 4, className = '', testId = 'platform-badges' }) {
  if (!platforms || platforms.length === 0) return null;
  const compact = size === 'sm';
  const inline = platforms.slice(0, maxInline);
  const overflow = platforms.length - inline.length;
  const isMulti = platforms.length >= 2;
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid={testId}>
      {isMulti && (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 font-semibold ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'}`}
          title="This product spans multiple platform types"
          data-testid={`${testId}-multi-indicator`}
        >
          <Layers className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {platforms.length}×
        </span>
      )}
      {inline.map(p => (
        <span
          key={p.id}
          className={`inline-flex items-center gap-1 rounded-full border bg-card ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'}`}
          data-testid={`${testId}-item-${p.id}`}
        >
          <span
            className={`inline-block rounded-full shrink-0 ${compact ? 'h-1 w-1' : 'h-1.5 w-1.5'}`}
            style={{ backgroundColor: p.category_color || '#8b5cf6' }}
          />
          {p.name || p.id}
        </span>
      ))}
      {overflow > 0 && (
        <span className={`text-muted-foreground ${compact ? 'text-[9px]' : 'text-[10px]'}`}>+{overflow} more</span>
      )}
    </div>
  );
}
